import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import { Turnstile, type TurnstileInstance } from "@marsidev/react-turnstile";
import { getTurnstileSiteKey } from "@/lib/turnstile/config";

export type TurnstileWidgetHandle = {
  /** Limpa o widget e zera o token no consumidor. O token é de uso único. */
  reset: () => void;
};

type TurnstileWidgetProps = {
  /** Recebe o token quando resolve, e `null` quando expira, falha ou reseta. */
  onToken: (token: string | null) => void;
  /**
   * Disparado quando o widget fica indisponível: falha no carregamento do
   * script (adblock, rede corporativa, Site Key errada, CF fora), browser não
   * suportado, ou nenhum token em `TURNSTILE_AVAILABILITY_TIMEOUT_MS`. Sem
   * isso, o botão fica desabilitado pra sempre e em silêncio — não há
   * `onError` do widget nesses casos, porque o widget nunca chegou a existir.
   */
  onUnavailable?: () => void;
  className?: string;
};

/** Tempo máximo esperando um token antes de considerar o captcha indisponível. */
export const TURNSTILE_AVAILABILITY_TIMEOUT_MS = 15_000;

/**
 * Wrapper do Turnstile. Renderiza `null` quando a site key não está
 * configurada — assim dev local e a suíte Jest rodam sem conta Cloudflare.
 */
export const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  function TurnstileWidget({ onToken, onUnavailable, className }, ref) {
    const innerRef = useRef<TurnstileInstance>(null);
    const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const siteKey = getTurnstileSiteKey();

    const clearAvailabilityTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const notifyUnavailable = () => {
      clearAvailabilityTimeout();
      onUnavailable?.();
    };

    // Captcha habilitado e o WIDGET (script) não carrega em 15s = travou de
    // verdade. Importante: isso mede "o script carregou?", não "chegou
    // token?" — no modo Managed a Cloudflare mostra um checkbox interativo
    // pra parte do tráfego, e aí o token só chega quando o usuário clica.
    // Por isso a limpeza acontece em onWidgetLoad (widget renderizado com
    // sucesso), não em onSuccess — um usuário lento resolvendo o desafio não
    // pode disparar um falso "indisponível".
    useEffect(() => {
      if (!siteKey) return;
      timeoutRef.current = setTimeout(notifyUnavailable, TURNSTILE_AVAILABILITY_TIMEOUT_MS);
      return () => clearAvailabilityTimeout();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteKey]);

    useImperativeHandle(
      ref,
      () => ({
        reset: () => {
          innerRef.current?.reset();
          onToken(null);
        },
      }),
      [onToken]
    );

    if (!siteKey) return null;

    return (
      <Turnstile
        ref={innerRef}
        siteKey={siteKey}
        className={className}
        options={{ language: "pt-br", theme: "light" }}
        onWidgetLoad={() => clearAvailabilityTimeout()}
        onSuccess={(token: string) => onToken(token)}
        onExpire={() => onToken(null)}
        onError={() => onToken(null)}
        onUnsupported={() => notifyUnavailable()}
        scriptOptions={{ onError: () => notifyUnavailable() }}
      />
    );
  }
);
